import ServiceLanding, { type ServiceLandingProps } from "@/components/feature/ServiceLanding";
import { buildBreadcrumbSchema, buildServiceSchema } from "@/seo/schema";
import { SITE_URL } from "@/pages/home/constants";

const PAGE_URL = `${SITE_URL}aluguel-de-lancha-capitolio`;

const data: ServiceLandingProps = {
  seo: {
    title: "Aluguel de Lancha em Capitólio | Locação no Lago de Furnas",
    description:
      "Aluguel de lancha em Capitólio com locação particular no Lago de Furnas. Escolha a lancha ideal para o seu grupo, com ou sem marinheiro. Reserve no WhatsApp.",
    keywords:
      "aluguel de lancha em Capitólio, locação de lancha Capitólio, alugar lancha Lago de Furnas, lancha para grupos Capitólio, lancha com marinheiro Capitólio",
    canonical: PAGE_URL,
    jsonLd: [
      buildServiceSchema({
        name: "Aluguel de lancha em Capitólio",
        description:
          "Locação de lancha particular em Capitólio para famílias, casais e grupos de até 19 passageiros, com ou sem marinheiro, no Lago de Furnas.",
        url: PAGE_URL,
        serviceType: "Aluguel de lancha",
      }),
      buildBreadcrumbSchema([
        { name: "Início", url: SITE_URL },
        { name: "Aluguel de Lancha em Capitólio", url: PAGE_URL },
      ]),
    ],
  },
  breadcrumbLabel: "Aluguel de Lancha em Capitólio",
  tagline: "Sua lancha, seu roteiro",
  h1: "Aluguel de Lancha em Capitólio",
  intro:
    "Alugue uma lancha particular e explore o Lago de Furnas no seu próprio ritmo. A Eco Tour Turismo coloca à disposição uma frota moderna, com opções de 12 a 19 passageiros, para famílias, casais e grupos que querem privacidade total no Mar de Minas — com ou sem marinheiro.",
  heroImage:
    "https://readdy.ai/api/search-image?query=Modern%20private%20speedboat%20moored%20on%20calm%20turquoise%20lake%20water%20with%20lush%20green%20mountains%20and%20canyon%20cliffs%20in%20Capitolio%20Brazil%2C%20bright%20sunny%20day%2C%20elegant%20travel%20photography%2C%20high%20detail&width=1600&height=900&seq=svc-aluguel-hero-v1&orientation=landscape",
  heroAlt: "Aluguel de lancha em Capitólio com lancha particular ancorada no Lago de Furnas",
  heroMobileBanner: true,
  reviewsSubtitle:
    "Clientes que já alugaram lancha em Capitólio com a Eco Tour Turismo — avaliações reais do Google e do TripAdvisor.",
  furnasSections: true,
  showGallery: false,
  priceNote: "A partir de R$ 700 por lancha",
  highlights: [
    { icon: "ri-user-star-line", title: "Com ou sem marinheiro", description: "Você escolhe como pilotar" },
    { icon: "ri-team-line", title: "De 12 a 19 passageiros", description: "Lancha ideal para seu grupo" },
    { icon: "ri-time-line", title: "Horário flexível", description: "Locação por meio período ou dia" },
  ],
  featuresTitle: "Por que alugar uma lancha com a Eco Tour Turismo",
  featuresSubtitle:
    "Locação de lancha em Capitólio com frota revisada, estrutura completa e atendimento próximo do início ao fim.",
  features: [
    {
      icon: "ri-ship-2-line",
      title: "Frota moderna e variada",
      description:
        "Quatro modelos de lancha, de 12 a 19 passageiros, para casais, famílias e grupos grandes — todas revisadas e equipadas.",
    },
    {
      icon: "ri-settings-3-line",
      title: "Locação flexível",
      description:
        "Alugue por meio período ou dia inteiro, com roteiro livre pelos atrativos do Lago de Furnas.",
    },
    {
      icon: "ri-shield-check-line",
      title: "Segurança garantida",
      description:
        "Embarcações homologadas, equipamentos de segurança e, se preferir, marinheiro experiente para conduzir o passeio.",
    },
  ],
  stepsTitle: "Como funciona o aluguel de lancha em Capitólio",
  stepsSubtitle: "Simples e transparente, do primeiro contato ao embarque no Lago de Furnas.",
  steps: [
    {
      icon: "ri-whatsapp-line",
      title: "Solicite o orçamento",
      description: "Conte para a nossa equipe a data, o número de pessoas e o tempo desejado.",
    },
    {
      icon: "ri-sailboat-line",
      title: "Escolha a sua lancha",
      description: "Indicamos o modelo ideal para o seu grupo e confirmamos a disponibilidade.",
    },
    {
      icon: "ri-anchor-line",
      title: "Retire e navegue",
      description: "Embarque no Complexo Furnas Aventura e aproveite o Lago de Furnas com total liberdade.",
    },
  ],
  galleryTitle: "A frota disponível para aluguel",
  gallerySubtitle: "Lanchas confortáveis e equipadas para você curtir o Lago de Furnas do jeito que quiser.",
  gallery: [
    {
      image:
        "https://static.readdy.ai/image/2b7eadfcd80fca33a6975f7d0c97ecdf/753722eae528390a97a79bb8866c6692.jpeg",
      alt: "Lancha Lake Hunter para 14 passageiros em Capitólio no Lago de Furnas",
      caption: "Lake Hunter — 14 passageiros",
    },
    {
      image:
        "https://static.readdy.ai/image/2b7eadfcd80fca33a6975f7d0c97ecdf/89f3bedef1a0837bd6c69ec11ebb3896.jpeg",
      alt: "Lancha Vall para 15 passageiros em Capitólio no Lago de Furnas",
      caption: "Vall — 15 passageiros",
    },
    {
      image:
        "https://static.readdy.ai/image/2b7eadfcd80fca33a6975f7d0c97ecdf/d005a26d7d040b967b0571c5a60b7c3f.jpeg",
      alt: "Lancha Luna para 12 passageiros em Capitólio no Lago de Furnas",
      caption: "Luna — 12 passageiros",
    },
    {
      image:
        "https://static.readdy.ai/image/2b7eadfcd80fca33a6975f7d0c97ecdf/70dd17502845a3544895db9af7128fbc.jpeg",
      alt: "Lancha Dream Sea para 19 passageiros com banheiro em Capitólio",
      caption: "Dream Sea — 19 passageiros",
    },
    {
      image:
        "https://static.readdy.ai/image/2b7eadfcd80fca33a6975f7d0c97ecdf/bd41190ddd61365610701b4e77a1b8e7.jpeg",
      alt: "Cânions de Capitólio no roteiro de aluguel de lancha no Lago de Furnas",
      caption: "Roteiro pelos Cânions",
    },
    {
      image:
        "https://static.readdy.ai/image/2b7eadfcd80fca33a6975f7d0c97ecdf/48918e557a883f272772a4c36c5dda87.jpeg",
      alt: "Lagoa Azul de Capitólio visitada em aluguel de lancha",
      caption: "Parada na Lagoa Azul",
    },
  ],
  faqs: [
    {
      question: "Quanto custa alugar uma lancha em Capitólio?",
      answer:
        "A locação de lancha em Capitólio começa a partir de R$ 700 por lancha. O valor final depende do modelo, do tempo de locação e do roteiro escolhido — fale com a nossa equipe pelo WhatsApp para um orçamento personalizado.",
    },
    {
      question: "Posso alugar a lancha sem marinheiro?",
      answer:
        "Sim. Oferecemos a locação com ou sem marinheiro. Se você preferir conduzir, orientamos sobre a habilitação necessária e as regras de navegação no Lago de Furnas.",
    },
    {
      question: "Quantas pessoas cabem na lancha alugada?",
      answer:
        "Temos lanchas para 12, 14, 15 e 19 passageiros. Assim conseguimos atender tanto casais e grupos pequenos quanto famílias e turmas maiores.",
    },
    {
      question: "A lancha alugada já vem com equipamentos?",
      answer:
        "Sim. Nossas lanchas contam com itens de segurança obrigatórios, e vários modelos têm som Bluetooth, cooler e, em alguns casos, churrasqueira e banheiro a bordo.",
    },
    {
      question: "Posso escolher o roteiro da locação?",
      answer:
        "Claro. No aluguel de lancha o roteiro é livre: você decide o ritmo e os pontos que quer visitar, como os Cânions de Capitólio, a Lagoa Azul e os bares flutuantes.",
    },
  ],
  ctaTitle: "Alugue a sua lancha no Lago de Furnas",
  ctaText:
    "Fale com a Eco Tour Turismo e garanta a lancha ideal para o seu grupo em Capitólio. Atendimento rápido e personalizado pelo WhatsApp.",
  relatedCtaLabel: "Ver site completo",
  related: [
    {
      to: "/passeio-de-jet-ski-capitolio",
      title: "Passeio de Jet Ski em Capitólio",
      description: "Adrenalina no Lago de Furnas com passeio guiado e locação de moto aquática.",
      icon: "ri-speed-up-line",
    },
  ],
};

export default function AluguelDeLanchaCapitolio() {
  return <ServiceLanding {...data} />;
}